import React, { useEffect, useState } from "react";
import { PropsWithoutRef } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { SIDEBAR_WIDTH } from "../constants";
import useAuth from "../hooks/useAuth";
import { ROLES } from "../utils/enums";
import useTranslation from "../hooks/traslation";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const userData = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const translation = useTranslation();

  const items = [
    {
      label: translation.dashboard.dashboard,
      name: "Dashboard",
      image: "/static/images/dashboard_icon.svg",
      key: 1,
      selected: "/dashboard",
    },
    {
      label: translation.main.ManageEmployer,
      name: "Manage Employers",
      image: "/static/images/user_icon.svg",
      key: 2,
      selected: "/manage-employers",
    },
    {
      label: translation.main.manageFreelancer,
      name: "Manage Freelancers",
      image: "/static/images/people_icon.svg",
      key: 3,
      selected: "/manage-freelancer",
    },
    {
      label: "Manage Categories",
      name: "Manage Categories",
      image: "/static/images/category_icon.svg",
      key: 4,
      selected: "/manage-categories",
    },
    {
      label: "Manage Tasks",
      name: "Manage Tasks",
      image: "/static/images/tick_square_icon.svg",
      key: 5,
      selected: "/manage-tasks",
    },
    {
      label: translation.main.manageRefund,
      name: "Manage Refunds",
      image: "/static/images/offer_icon.svg",
      key: 6,
      selected: "/manage-dispute-refunds",
    },
    {
      label: "Manage Payout",
      name: "Manage Payout",
      image: "/static/images/money.svg",
      key: 7,
      selected: "/manage-payout",
    },
    {
      label: "Manage Wallet",
      name: "Manage Wallet",
      image: "/static/images/walet.svg",
      key: 8,
      selected: "/manage-wallet",
    },

    {
      label: translation.main.managePayment,
      name: "Manage Payment",
      image: "/static/images/money_icon.svg",
      key: 9,
      selected: "/manage-payments",
    },
    {
      label: translation.main.manageSubadmin,
      name: "Manage Sub-Admin",
      image: "/static/images/subadmin_icon.svg",
      key: 10,
      selected: "/manage-subadmin",
    },
    {
      label: translation.main.manageNotification,
      name: "Manage Notifications",
      image: "/static/images/notification2_icon.svg",
      key: 11,
      selected: "/manage-notifications",
    },
    {
      label: "Manage Ratings",
      name: "Manage Reviews",
      image: "/static/images/rating_icon.svg",
      key: 12,
      selected: "/manage-reviews",
    },
    {
      label: translation.main.manageCms,
      name: "Manage CMS",
      image: "/static/images/cms_icon.svg",
      key: 13,
      selected: "/manage-cms",
    },
  ];

  const filteredSidebarItems = items
    ?.filter((item) => {
      if (!item?.key) return false; // Ensure item has a module before filtering

      // Find the permission for the module
      const permission = userData?.accessRoleId?.permission?.find(
        (perm: any) => perm?.sideBarId === item?.key
      );

      return !!permission?.can_view; // Ensure boolean return value
    })
    .filter(Boolean);

  const [list, setList] = useState<any>(
    userData?.role === ROLES.ADMIN ? items : filteredSidebarItems
  );

  useEffect(() => {
    if (userData?.role === ROLES.ADMIN) {
      setList(items);
    } else if (userData?.accessRoleId?.permission?.length) {
      const permission = userData?.accessRoleId?.permission;

      const filteredItems = items.filter((item) => {
        if (!item?.key) return false;

        const matchedPermission = permission.find(
          (perm) => perm?.sideBarId === item?.key && perm.isView
        );

        return !!matchedPermission;
      });

      setList(filteredItems);
    }
  }, [userData]);

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { lg: 0 } }}
      className="sidebar-main-div"
    >
      <Drawer
        className="sidebar_drawer sidebar-main-div"
        variant="temporary"
        open={mobileOpen}
        onClose={handleSidebarToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure style={{ width: "180px", height: "60px", margin: "auto" }}>
            <img
              style={{ width: "100%", height: "100%" }}
              src="/static/images/logo.png"
              alt=""
              onClick={() => navigate("/dashboard")}
            />
          </figure>
          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <List className="sidebr-lst">
          {list?.length
            ? list
                .filter((ele: any) => ele !== undefined)
                .map((item: any) => {
                  const isSelected = location.pathname.includes(item?.selected);
                  return (
                    <ListItemButton
                      key={item?.label}
                      className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                      onClick={() => navigate(item?.selected)}
                    >
                      <ListItemIcon>
                        <img src={item?.image} alt="Icon" />
                      </ListItemIcon>
                      <ListItemText
                        className="lstitm-txt"
                        primary={item?.label}
                      />
                    </ListItemButton>
                  );
                })
            : null}
        </List>
      </Drawer>
      <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img
              src="/static/images/logo.png"
              alt=""
              onClick={() => navigate("/dashboard")}
            />
          </figure>
          {/* <h1>Logo</h1> */}
          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>

        <List className="sidebr-lst" sx={{ flexGrow: 1 }}>
          {list?.length
            ? list
                .filter((ele: any) => ele !== false && ele !== undefined)
                .map((item: any) => {
                  const isSelected = location.pathname.includes(item?.selected);
                  return (
                    <ListItemButton
                      key={item?.label}
                      className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                      selected={
                        location.pathname.includes(item.selected) ===
                        item.selected
                      }
                      onClick={() => navigate(item?.selected)}
                    >
                      <ListItemIcon>
                        <img src={item?.image} alt="Icon" />
                      </ListItemIcon>
                      <ListItemText
                        className="lstitm-txt"
                        primary={item?.label}
                      />
                    </ListItemButton>
                  );
                })
            : null}
        </List>
      </Drawer>
    </Box>
  );
}
